import React, { useEffect, useRef } from "react";

const EAST_BOUNDS = { minLat: 24.0, maxLat: 28.8, minLng: 45.5, maxLng: 51.2 };
const DAMMAM = [26.4207, 50.0888];
export const isEastern = (lat, lng) =>
  lat >= EAST_BOUNDS.minLat && lat <= EAST_BOUNDS.maxLat &&
  lng >= EAST_BOUNDS.minLng && lng <= EAST_BOUNDS.maxLng;

// Lightweight Leaflet wrapper using the global window.L (loaded from CDN in index.html)
export default function MapView({ markers = [], center, zoom = 12, line, className = "", onPick, easternOnly = false, onOutside }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const pickRef = useRef(onPick); pickRef.current = onPick;
  const outsideRef = useRef(onOutside); outsideRef.current = onOutside;
  const eastRef = useRef(easternOnly); eastRef.current = easternOnly;

  useEffect(() => {
    const L = window.L;
    if (!L || !ref.current) return;
    if (!mapRef.current) {
      const c = center || (markers[0] && [markers[0].lat, markers[0].lng]) || (easternOnly ? DAMMAM : [24.7136, 46.6753]);
      mapRef.current = L.map(ref.current, { zoomControl: true, attributionControl: false }).setView(c, zoom);
      L.tileLayer("https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", { maxZoom: 20, subdomains: ["0", "1", "2", "3"] }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
      if (easternOnly) {
        mapRef.current.setMaxBounds([[EAST_BOUNDS.minLat, EAST_BOUNDS.minLng], [EAST_BOUNDS.maxLat, EAST_BOUNDS.maxLng]]);
      }
      mapRef.current.on("click", (e) => {
        const { lat, lng } = e.latlng;
        if (eastRef.current && !isEastern(lat, lng)) { outsideRef.current && outsideRef.current(); return; }
        if (pickRef.current) pickRef.current(lat, lng);
      });
    }
    const layer = layerRef.current;
    layer.clearLayers();
    const pts = [];
    markers.forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      const icon = L.divIcon({
        className: "",
        html: `<div style="position:relative"><div style="background:${m.color || "#2563EB"};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.3)"></div><div style="position:absolute;top:8px;left:8px;color:#fff;transform:rotate(0)">${m.emoji || ""}</div></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      });
      const mk = L.marker([m.lat, m.lng], { icon }).addTo(layer);
      if (m.label) mk.bindPopup(`<div style="font-family:'IBM Plex Sans Arabic'">${m.label}</div>`);
      pts.push([m.lat, m.lng]);
    });
    if (line && line.length > 1) {
      L.polyline(line, { color: "#F97316", weight: 4, dashArray: "8 8" }).addTo(layer);
    }
    if (pts.length > 1) {
      mapRef.current.fitBounds(pts, { padding: [50, 50] });
    } else if (pts.length === 1) {
      mapRef.current.setView(pts[0], zoom);
    }
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 150);
  }, [markers, center, zoom, line]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  return <div ref={ref} className={`w-full rounded-2xl overflow-hidden border border-border ${className}`} style={{ minHeight: 260 }} data-testid="map-view" />;
}
